import {faFolder} from "@fortawesome/pro-solid-svg-icons/faFolder";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {MailItemType} from "@twigsby/client-data";
import {MailFile} from "@twigsby/client-data/lib/Types/MailFileTypes";
import {
	Badge,
	ColourOutline,
	ColourSolid,
	FlexGap,
	FlexJustify,
	ItemRow,
	ItemRowSize,
	Pill,
	PillSize,
	StackVertical,
	useLayout,
	useTranslation
} from "@twigsby/frontend-framework";
import {CustomCheckbox} from "@twigsby/frontend-framework/lib/Components/Forms/Toggle/ToggleSwitchCustom";
import React, {memo, MutableRefObject} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMailbox} from "../../Providers/NewMailboxProvider";
import {useSite} from "../../Providers/SiteInterfaceProvider";
import {MailItemThumbnails, MailPartTypeEnum} from './MailItemThumbnails';
import {MailTagCollection} from "./MailTagCollection";

interface MailItemComponentType extends MailItemType {
	selected? : boolean;
	selectable? : boolean;
	index? : string;
	callBack?: any;
	onDragStart?: any;
	onDragEnd?: any;
	onDragOver?: any;
	draggable?: boolean;
	shiftKey?: MutableRefObject<boolean>;
}

type MailITemThumbnailsType = {
	files: Array<MailFile>;
	onClickCallback: (link?: string) => void;
	largeThumbnail?: boolean;
}

const MailItemThumbs = (props: MailITemThumbnailsType) => {
	const { isSmall } = useLayout();

	return (
		<>
			<MailItemThumbnails
				mailType={MailPartTypeEnum['Mail-Envelopes']}
				files={props.files}
				onClickCallback={(link: string) => props.onClickCallback(link)}
				largeThumbnail={props.largeThumbnail}
			/>
			<MailItemThumbnails
				mailType={MailPartTypeEnum['Mail-Contents']}
				files={props.files}
				onClickCallback={(link: string) => props.onClickCallback(link)}
				hideMissing={!isSmall}
				largeThumbnail={props.largeThumbnail}
			/>
		</>
	)
}

const MailItem = memo((props: MailItemComponentType) => {
	const navigate = useNavigate();

	const { t } = useTranslation();
	const { mailBoxInfo } = useMailbox();

	const { setPdfViewer } = useSite();
	const { isSmall, isMedium, isLarge, isXL, isXXL } = useLayout();

	if(props.itemHidden) {
		return null;
	}

	const TopUpRequired = props.files[0]?.topupRequired || props.files[1]?.topupRequired;

	const HandleClick = (e: React.MouseEvent) => {
		if(props.shiftKey?.current) {
			props.callBack(props.mailUuid, !props.selected);
		} else {
			const itemLink = props.clientFolder ? `custom-folder/${props.clientFolder.id}` : `${mailBoxInfo?.folderSlug}/${props.mailUuid}`;
			navigate(`/mail/${mailBoxInfo?.folderSlug === 'tagged' ? `inbox/${props.mailUuid}` : itemLink}`)
		}
	}

	return (
		<ItemRow
			size={ItemRowSize.Large}
			highlighted={!props.dateRead}
			muted={props.dateRead !== null}
			selected={props.selected}
			extraProps={{
				'draggable': props.draggable,
				'onDragStart': (e: any) => props.onDragStart(e),
				'onDragEnd': (e: any) => props.onDragEnd(e),
				'onDragOver': (e: any) => props.onDragOver(e),
				'data-item-id': props.mailUuid
			}}
		>
			<div className={`item-row-checkbox`}
				 draggable={false}
				 onClick={(e) => e.stopPropagation()}
			>
				{props.selectable && (
					<CustomCheckbox
						onChange={(e) => props.callBack(props.mailUuid, e)}
						label={props.mailUuid}
						name={`mailItem${props.mailUuid}`}
						hideLabel={true}
						defaultChecked={props.selected}
					/>
				)}
			</div>
			<div
				onClick={(e) => HandleClick(e)}
				className={'mailbox-item-meta'}
				draggable={false}
			>
				{!isSmall && (
					<div className={props.files.length > 1 ? `thumbnail-stack` : ''}>
						<MailItemThumbs
							files={props.files}
							onClickCallback={(link) => setPdfViewer(true, link)}
						/>
					</div>
				)}

				{isSmall && (
					<MailItemThumbs
						files={props.files}
						onClickCallback={(link) => setPdfViewer(true, link)}
					/>
				)}

				<div className='flex-row flex-gap-sm mail-item-reference'>
					{!props.dateRead && <Badge colour={ColourSolid.Primary} />}
					<StackVertical gap={FlexGap.ExtraSmall} justify={FlexJustify.Start}>
						{props.dateRead && (
							<span className='mail-item-tracking-id'>{props.trackingId}</span>
						)}
						{!props.dateRead && (
							<strong className='mail-item-tracking-id'>{props.trackingId}</strong>
						)}

						{TopUpRequired && (
							<Pill content={t('mailItem.labels.topUpRequired')} size={PillSize.Small} colour={ColourOutline.Alert} />
						)}

						{props.mailLocationId !== 20 && props.mailAction?.requiresUserInput && (
							<Pill content={'Item Locked'} size={PillSize.Small} colour={ColourOutline.Alert} />
						)}

						{!TopUpRequired && props.clientFolder?.description && (
							<div className={`pill pill-sm flex-gap-xs ${props.clientFolder?.colour ? `${props.clientFolder?.colour}` : ColourSolid.Secondary}`}>
								<FontAwesomeIcon icon={faFolder} fixedWidth={true} />
								{props.clientFolder?.description}
							</div>
						)}

						{!TopUpRequired && props.daysUntilNextStorageCharge && (
							<Pill
								content={t('mailItem.labels.storageCharges', { number: props.daysUntilNextStorageCharge})}
								size={PillSize.Small}
								colour={props?.daysUntilNextStorageCharge && props?.daysUntilNextStorageCharge <= 5 ? ColourOutline.Alert : ColourOutline.Secondary}
							/>
						)}

						{!TopUpRequired && props.forwardingBatchId && (
							<Pill content={'View Batch'} size={PillSize.Small} colour={ColourOutline.Secondary} />
						)}
					</StackVertical>
				</div>

				{isMedium && (
					<div className='mail-item-pages'>
						{mailBoxInfo?.folderSlug === 'pending' && (
							<>{props.mailAction?.description}</>
						)}

						{mailBoxInfo?.folderSlug !== 'pending' && (
							<>{props.totalPages ? t('counts.page', { count: props.totalPages }) : t('mailItem.labels.unopened')}</>
						)}
					</div>
				)}

				{isXL && (
					<div className='mail-item-tracking-numbers'>
						{props.incomingTrackingId && (
							<Pill content={`${t('mailItem.labels.trackingIdInShout')}: ${props.incomingTrackingId}`} />
						)}

						{props.outgoingTrackingId && (
							<Pill content={`${t('mailItem.labels.trackingIdOutShort')}: ${props.outgoingTrackingId}`} />
						)}
					</div>
				)}
				{isLarge && (
					<div className='mail-item-tags item-row-tags'>
						{props.tags && (
							<MailTagCollection tags={props.tags} small={true} displayLimit={isXXL ? 5 : 3} linkSlug={'/mail/tagged/'} />
						)}
					</div>
				)}
				{isXL && (
					<div className='mail-item-notes'>
						<div className={'notes-content-wrapper'}>{props.note}</div>
					</div>
				)}
			</div>
		</ItemRow>
	);
});

export default MailItem;
